import { Injectable, inject } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { TodoEntryModel } from '../../models/todo-planner/todo-entry.model';
import {
  NotificationType,
  ShiveNotificationService,
} from '../../services/controls/shive-notification.service';
import * as fromTodoPlannerEntryActions from './todo-planner.actions';

@Injectable({
  providedIn: 'root',
})
export class TodoPlannerHttpService {
  private readonly store = inject(Store);
  private readonly shiveNotificationService = inject(ShiveNotificationService);
  private readonly actions$ = inject(Actions);

  constructor() {
    this.handleHttpCallbacks();
  }

  public getTodoPlannerEntries(
    start: Date,
    end: Date,
    userId: number,
  ): Observable<TodoEntryModel[]> {
    this.store.dispatch(
      fromTodoPlannerEntryActions.fetchTodoPlannerEntries({
        payload: { start, end, userId },
      }),
    );

    return this.actions$.pipe(
      ofType(fromTodoPlannerEntryActions.setTodoPlannerEntries),
      map((result) => result.payload),
      take(1),
    );
  }

  public getSingleTodoPlannerEntry(entryId: number): Observable<TodoEntryModel> {
    this.store.dispatch(
      fromTodoPlannerEntryActions.fetchSingleTodoPlannerEntry({
        payload: entryId,
      }),
    );

    return this.actions$.pipe(
      ofType(fromTodoPlannerEntryActions.setSingleTodoPlannerEntry),
      map((result) => result.payload),
      take(1),
    );
  }

  public addTodoPlannerEntry(entryModel: TodoEntryModel): Observable<TodoEntryModel> {
    this.store.dispatch(
      fromTodoPlannerEntryActions.addTodoPlannerEntry({
        payload: entryModel,
      }),
    );

    return this.actions$.pipe(ofType(fromTodoPlannerEntryActions.todoPlannerEntryAdded));
  }

  public updateTodoPlannerEntry(entryModel: TodoEntryModel): Observable<TodoEntryModel> {
    this.store.dispatch(
      fromTodoPlannerEntryActions.updateTodoPlannerEntry({
        payload: {
          todoEntryModel: entryModel,
        },
      }),
    );

    return this.actions$.pipe(
      ofType(fromTodoPlannerEntryActions.todoPlannerEntryUpdated),
      map((response) => response.payload),
      take(1),
      map((payload) => payload.todoEntryModel),
    );
  }

  public updateMultipleTodoEntries(allEntries: TodoEntryModel[]): Observable<TodoEntryModel[]> {
    this.store.dispatch(
      fromTodoPlannerEntryActions.updateMultipleTodoPlannerEntries({
        payload: {
          todoEntryModels: allEntries,
        },
      }),
    );

    return this.actions$.pipe(
      ofType(fromTodoPlannerEntryActions.todoPlannerMultipleEntriesUpdated),
      map((response) => response.payload),
      take(1),
      map((payload) => payload.todoEntryModels),
    );
  }

  public deleteTodoPlannerEntry(toBeDeletedEntry: TodoEntryModel): Observable<TodoEntryModel> {
    this.store.dispatch(
      fromTodoPlannerEntryActions.deleteTodoPlannerEntry({
        payload: toBeDeletedEntry,
      }),
    );

    return this.actions$.pipe(ofType(fromTodoPlannerEntryActions.todoPlannerEntryDeleted));
  }

  private handleHttpCallbacks(): void {
    this.actions$
      .pipe(
        ofType(
          fromTodoPlannerEntryActions.todoPlannerEntryAdded,
          fromTodoPlannerEntryActions.todoPlannerEntryUpdated,
          fromTodoPlannerEntryActions.todoPlannerMultipleEntriesUpdated,
          fromTodoPlannerEntryActions.todoPlannerEntryDeleted,
        ),
      )
      .subscribe(() => {
        this.shiveNotificationService.show(NotificationType.Success);
      });
  }
}
